// src/notifications/components/notification-row.tsx
// One notification row. Faithful to the Flutter `NotificationCenterRowWidget`:
//  - an app-icon avatar on the left,
//  - the HTML message body (falls back to the subject when message is empty),
//  - a relative "time ago" line,
//  - an unread highlight when `isRead !== true` (source: designColor14 card bg).
//
// The message arrives as HTML (source renders it via a `Html` widget), so it is
// sanitised with DOMPurify before render — the app already ships dompurify.
// Colour is DS tokens only; unread tint uses --color-status-info-bg (the token
// mapping of the source's light-cyan #E0F2F6 unread card).
import { useEffect, useRef, type RefObject } from 'react';
import DOMPurify from 'dompurify';
import { type NotificationModel } from '@/notifications/types/notification';
import { getNotificationDateFormat } from '@/notifications/lib/notification-time';
import { Bell } from 'lucide-react';

/** Source parity: Flutter marks a row read once it clears ~80% visibility. */
const VISIBILITY_THRESHOLD = 0.8;

export function NotificationRow({
  notification,
  scrollRootRef,
  onViewed,
}: {
  notification: NotificationModel;
  /** Scroll container used as the IntersectionObserver root (the panel body). */
  scrollRootRef?: RefObject<HTMLElement | null>;
  /** Called once when an UNREAD row crosses the visibility threshold. */
  onViewed?: (id: string) => void;
}) {
  const isRead = notification.isRead === true;
  // Source: `data.message ?? (data.subject ?? "")`.
  const rawHtml = notification.message ?? notification.subject ?? '';
  const safeHtml = DOMPurify.sanitize(rawHtml);

  // Only unread rows are tracked; a read row is already flushed. Mirrors the
  // Flutter VisibilityDetector that records ids of rows the practitioner
  // actually saw (>=80% visible) rather than every loaded row.
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isRead || !onViewed) return;
    const el = rowRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
            onViewed(notification.id);
            observer.disconnect();
            break;
          }
        }
      },
      { root: scrollRootRef?.current ?? null, threshold: VISIBILITY_THRESHOLD },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isRead, onViewed, notification.id, scrollRootRef]);

  // Showcase look, unconditional (design NotificationPanel `.row`): a white row with a hairline, the neutral bell
  // disc, the message on the body role (body-strong while unread), the time on the meta role, and a brand dot for
  // unread (never colour alone). `.notification-row*`, showcase-auth.css.
  return (
    <div
      ref={rowRef}
      className="notification-row"
      data-testid="notification-row-v2"
      data-unread={isRead ? undefined : 'true'}
    >
      <div className="notification-disc" aria-hidden="true">
        <Bell className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="notification-message"
          // Sanitised above; source renders the same field as HTML.
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
        <div className="notification-time">{getNotificationDateFormat(notification.createdOn)}</div>
      </div>
      {isRead ? null : (
        <span className="notification-dot" data-testid="notification-unread-dot">
          <span className="sr-only">Unread</span>
        </span>
      )}
    </div>
  );
}
